import { Mutation } from "@apollo/client/react/components";
import { Button, LoadingIndicator } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import React, { ReactElement } from "react";
import { useLocalStorage } from "react-use";
import RequestGovernmentOwnership from "~/lib/mutations/RequestGovernmentOwnership";
import {
  IRequestGovernmentOwnershipMutation,
  IRequestGovernmentOwnershipMutationVariables,
} from "~/lib/mutations/__generated__/RequestGovernmentOwnership.generated";
import { ICity, IGovernment } from "~/types/types";
import { FormattedMessage } from "react-intl";

interface IRequestAccessProps {
  government: Pick<IGovernment, "_id" | "viewerRequestedMembership" | "viewerIsMember" | "viewerIsOwner"> & {
    city: Pick<ICity, "name">;
  };
}

export default function RequestAccess({ government }: IRequestAccessProps): ReactElement {
  const [requests, setRequests] = useLocalStorage<string[]>("accessRequests", []);

  return (
    <div
      css={{
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Mutation<IRequestGovernmentOwnershipMutation, IRequestGovernmentOwnershipMutationVariables>
        variables={{
          governmentId: government._id,
        }}
        mutation={RequestGovernmentOwnership}
        onCompleted={() => {
          if (typeof requests !== "undefined") {
            setRequests(requests.concat([government._id]));
          }
        }}
      >
        {(requestAccess, { loading, called }) => {
          if (!process.browser) {
            return null;
          }

          const isButtonDisabled = typeof requests !== "undefined" && (requests.includes(government._id) || called);

          return (
            <div
              css={{
                padding: 24,
                borderRadius: 5,
                border: `1px solid ${palette.lightestGray}`,
                background: "#fff",
                display: "grid",
                gridRowGap: 16,
                textAlign: "center",
              }}
            >
              <h2
                css={{
                  fontSize: 24,
                  color: palette.darkGray,
                  fontWeight: 500,
                  margin: 0,
                }}
              >
                <FormattedMessage defaultMessage="Join {cityName}?" id="ZypfVK" values={{ cityName: government.city.name }} />
              </h2>

              <p
                css={{
                  margin: 0,
                  fontSize: 14,
                  color: palette.darkGray,
                }}
              >
                <FormattedMessage defaultMessage="Do you represent or work for this local government?" id="PDL48f" />
              </p>

              <Button
                theme="secondary"
                size="xsmall"
                onClick={requestAccess}
                fontSize={isButtonDisabled ? 14 : 16}
                color={isButtonDisabled ? "disabled" : palette.innovatorBlue}
                disabled={loading || isButtonDisabled}
                data-intercom-target="request access"
                style={{
                  fontWeight: "bold",
                  maxWidth: 400,
                  margin: "0 auto",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  pointerEvents: isButtonDisabled ? "none" : "all",
                }}
              >
                {loading ? (
                  <LoadingIndicator />
                ) : isButtonDisabled ? (
                  <FormattedMessage
                    defaultMessage="You've requested access to {cityName} 🎉" id="neqVmr"
                    values={{ cityName: government.city!.name }}
                  />
                ) : (
                  <FormattedMessage defaultMessage="Request Access" id="UnR8YN" />
                )}
              </Button>

              <p
                css={{
                  margin: 0,
                  fontSize: 12,
                  color: palette.sealBlue,
                }}
              >
                <FormattedMessage
                  defaultMessage="We may take up to 48 hours to review your request. If you have any questions about this process, <link>contact us</link>" id="p2yomL"
                  values={{
                    // eslint-disable-next-line react/display-name
                    link: (text: string) => {
                      return (
                        <a
                          css={{
                            color: palette.innovatorBlue,
                          }}
                          href="mailto:contact@govlaunch.com"
                        >
                          {text}
                        </a>
                      );
                    },
                  }}
                />
              </p>
            </div>
          );
        }}
      </Mutation>
    </div>
  );
}
